/**
 * CompletionReportPanel — Campaign Manager UI for end-of-campaign metric entry.
 *
 * Renders inside the BulkCampaignWorkspace as the "Completion Report" tab.
 * One row per creator with editable metric inputs. Save persists via
 * PATCH /api/campaigns/{row_id}/completion-metrics.
 *
 * Strictly additive — does not touch the pipeline / status / draft / live_url flows.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Loader2, Save, CheckCircle2, ExternalLink, TrendingUp, Eye, Heart, MessageCircle, Rocket,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtNum = (n) => {
  if (n == null || n === '') return '—';
  const v = Number(n);
  if (Number.isNaN(v)) return '—';
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1000) return `${(v / 1000).toFixed(1)}K`;
  return v.toLocaleString();
};

const toIntOrUndef = (v) => {
  if (v === '' || v == null) return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? Math.max(0, Math.floor(n)) : undefined;
};

/**
 * Loads the report from the brand-facing endpoint when accessible,
 * else falls back to GET /api/bulk/campaigns/:id/creators (CM scope).
 * For CM users we use the bulk creators list — that endpoint already
 * includes the completion_metrics field if it has been stored.
 */
export const CompletionReportPanel = ({ campaignId, creators, campaignStatus, onCampaignCompleted }) => {
  // creators came from bulk_routes (id = row_id). Use a local state map keyed
  // by row id so each row can be edited independently with optimistic UX.
  const [edits, setEdits] = useState({});           // row_id -> partial metrics dict
  const [saving, setSaving] = useState({});         // row_id -> bool
  const [metricsMap, setMetricsMap] = useState({}); // row_id -> stored metrics
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);

  // Hydrate from bulk creators payload (already passed by parent).
  // We mirror it locally so per-row save can update only the affected row
  // without forcing a full parent reload.
  useEffect(() => {
    const next = {};
    for (const c of creators || []) {
      next[c.id] = c.completion_metrics || {};
    }
    setMetricsMap(next);
    setLoading(false);
  }, [creators]);

  const updateField = (rowId, key, value) => {
    setEdits((prev) => ({
      ...prev,
      [rowId]: { ...(prev[rowId] || {}), [key]: value },
    }));
  };

  const saveRow = async (rowId) => {
    const e = edits[rowId] || {};
    const stored = metricsMap[rowId] || {};
    // Merge edits over stored values to preserve untouched fields
    const merged = { ...stored, ...e };

    const payload = {
      live_post_link: (merged.live_post_link || '').toString().trim() || null,
      posted_date:    (merged.posted_date || '').toString().trim() || null,
      views:    toIntOrUndef(merged.views),
      reach:    toIntOrUndef(merged.reach),
      likes:    toIntOrUndef(merged.likes),
      comments: toIntOrUndef(merged.comments),
      shares:   toIntOrUndef(merged.shares),
      saves:    toIntOrUndef(merged.saves),
      notes:    (merged.notes || '').toString() || null,
    };
    // Pydantic uses strict typing — strip Nones for cleanliness
    const clean = Object.fromEntries(Object.entries(payload).filter(([, v]) => v !== null && v !== undefined));
    if (Object.keys(clean).length === 0) {
      toast.error('Enter at least one metric');
      return;
    }

    setSaving((s) => ({ ...s, [rowId]: true }));
    try {
      const { data } = await axios.patch(
        `${API_URL}/api/campaigns/${rowId}/completion-metrics`,
        clean,
        { withCredentials: true },
      );
      setMetricsMap((m) => ({ ...m, [rowId]: data.completion_metrics }));
      setEdits((e2) => { const cp = { ...e2 }; delete cp[rowId]; return cp; });
      toast.success('Metrics saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving((s) => ({ ...s, [rowId]: false }));
    }
  };

  const rows = useMemo(() => creators || [], [creators]);

  // Header-level aggregates (read from metricsMap so they recompute after save)
  const aggregate = useMemo(() => {
    let reported = 0, views = 0, reach = 0, likes = 0, comments = 0;
    const ers = [];
    for (const c of rows) {
      const m = metricsMap[c.id] || {};
      if (m.live_post_link) reported += 1;
      views += Number(m.views || 0);
      reach += Number(m.reach || 0);
      likes += Number(m.likes || 0);
      comments += Number(m.comments || 0);
      if (m.engagement_rate != null) ers.push(Number(m.engagement_rate));
    }
    const avgER = ers.length ? (ers.reduce((a, b) => a + b, 0) / ers.length) : null;
    const pct = rows.length ? Math.round((reported / rows.length) * 1000) / 10 : 0;
    return { reported, total: rows.length, pct, views, reach, likes, comments, avgER };
  }, [rows, metricsMap]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-[#6B6B6B] dark:text-zinc-500" data-testid="completion-report-loading">
        <Loader2 className="animate-spin mr-2" size={16} /> Loading report…
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl p-12 text-center text-sm text-[#6B6B6B] dark:text-zinc-500" data-testid="completion-report-empty">
        No creators on this campaign yet.
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="completion-report-panel">
      {/* Live aggregates */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="completion-aggregates">
        <AggTile icon={CheckCircle2} label="Reported"  value={`${aggregate.reported}/${aggregate.total}`} sub={`${aggregate.pct}%`} color="#00C972" testid="report-tile-reported" />
        <AggTile icon={Eye}          label="Views"     value={fmtNum(aggregate.views)}    color="#06B6D4" testid="report-tile-views" />
        <AggTile icon={TrendingUp}   label="Reach"     value={fmtNum(aggregate.reach)}    color="#FFB800" testid="report-tile-reach" />
        <AggTile icon={Heart}        label="Likes"     value={fmtNum(aggregate.likes)}    color="#FF3B30" testid="report-tile-likes" />
        <AggTile icon={MessageCircle} label="Comments" value={fmtNum(aggregate.comments)} color="#A855F7" testid="report-tile-comments" />
        <AggTile icon={TrendingUp}   label="Avg ER"
                 value={aggregate.avgER != null ? `${aggregate.avgER.toFixed(2)}%` : '—'}
                 color="#FF451A" testid="report-tile-er" />
      </div>

      {/* Mark Campaign Complete — terminal action for the CM. Flips the
          brand_campaign status, which enables the brand-side report + PDF
          download + share link. */}
      <div
        className="bg-white dark:bg-[#131518] border border-black/[0.08] dark:border-white/5 rounded-2xl p-4 flex items-center justify-between gap-3 flex-wrap"
        data-testid="completion-mark-complete-bar"
      >
        <div className="min-w-0">
          <div className="text-sm font-semibold text-[#0F0F0F] dark:text-white">
            {campaignStatus === 'completed' ? 'Campaign Completed' : 'Mark Campaign Complete'}
          </div>
          <div className="text-[11px] text-[#6B6B6B] dark:text-zinc-500 font-mono mt-0.5">
            {campaignStatus === 'completed'
              ? 'Brand can now download the final report.'
              : 'Flips campaign status to completed and unlocks the brand\u2019s report + PDF/Excel downloads.'}
          </div>
        </div>
        {campaignStatus === 'completed' ? (
          <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold bg-[#00C972]/15 text-[#00C972] border border-[#00C972]/30">
            <CheckCircle2 size={12} /> Completed
          </span>
        ) : (
          <Button
            onClick={async () => {
              if (!window.confirm('Mark this campaign as complete? The brand will see the final report immediately.')) return;
              setCompleting(true);
              try {
                await axios.post(
                  `${API_URL}/api/bulk/campaigns/${campaignId}/complete`,
                  {},
                  { withCredentials: true },
                );
                toast.success('Campaign marked complete \u00b7 Brand notified');
                if (onCampaignCompleted) await onCampaignCompleted();
              } catch (err) {
                toast.error(err.response?.data?.detail || 'Failed to mark complete');
              } finally {
                setCompleting(false);
              }
            }}
            disabled={completing}
            className="bg-[#00C972] hover:bg-[#00B265] text-black rounded-full px-5 py-2.5 text-sm font-semibold inline-flex items-center gap-1.5 disabled:opacity-50"
            data-testid="completion-mark-complete-button"
          >
            {completing ? <Loader2 size={14} className="animate-spin" /> : <Rocket size={14} />}
            {completing ? 'Completing\u2026' : 'Mark Campaign Complete'}
          </Button>
        )}
      </div>

      <p className="text-[11px] text-[#6B6B6B] dark:text-zinc-500 font-mono">
        Enter post-campaign metrics for each creator. Engagement rate is auto-computed from likes + comments ÷ reach (or followers).
      </p>

      <div className="space-y-2" data-testid="completion-rows">
        {rows.map((c) => {
          const stored = metricsMap[c.id] || {};
          const e = edits[c.id] || {};
          const merged = { ...stored, ...e };
          const isDirty = !!edits[c.id];
          const hasLink = !!merged.live_post_link;

          return (
            <div
              key={c.id}
              className={`bg-white dark:bg-[#0A0B0D] rounded-xl p-4 border transition-colors ${
                hasLink ? 'border-[#00C972]/20' : 'border-black/[0.08] dark:border-white/5'
              }`}
              data-testid={`completion-row-${c.id}`}
            >
              <div className="flex items-center gap-3 mb-3 flex-wrap">
                <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#FF451A] to-[#0055FF] flex items-center justify-center text-[#0F0F0F] dark:text-white text-sm font-bold shrink-0">
                  {c.creator_name?.[0]?.toUpperCase() || 'C'}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-[#0F0F0F] dark:text-white truncate">{c.creator_name}</div>
                  {c.instagram_handle && (
                    <div className="text-[11px] text-[#6B6B6B] dark:text-zinc-500">
                      @{c.instagram_handle} · {fmtNum(c.instagram_followers)} followers
                    </div>
                  )}
                </div>
                {hasLink && (
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-mono uppercase tracking-wider bg-[#00C972]/10 text-[#00C972] border border-[#00C972]/30 inline-flex items-center gap-1">
                    <CheckCircle2 size={9} /> Reported
                  </span>
                )}
                {stored.engagement_rate != null && (
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-mono uppercase tracking-wider bg-[#FF451A]/10 text-[#FF451A] border border-[#FF451A]/20">
                    ER {stored.engagement_rate}%
                  </span>
                )}
              </div>

              {/* Inputs */}
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2 mb-2">
                <LabeledInput
                  label="Live Post Link"
                  testid={`metric-link-${c.id}`}
                  value={merged.live_post_link || ''}
                  onChange={(v) => updateField(c.id, 'live_post_link', v)}
                  placeholder="https://instagram.com/p/..."
                  className="col-span-1 sm:col-span-2 md:col-span-2"
                />
                <LabeledInput
                  label="Posted Date"
                  testid={`metric-date-${c.id}`}
                  value={merged.posted_date || ''}
                  onChange={(v) => updateField(c.id, 'posted_date', v)}
                  placeholder="YYYY-MM-DD"
                  type="date"
                />
                <LabeledInput
                  label="Views"
                  testid={`metric-views-${c.id}`}
                  value={merged.views ?? ''}
                  onChange={(v) => updateField(c.id, 'views', v)}
                  type="number"
                />
                <LabeledInput
                  label="Reach"
                  testid={`metric-reach-${c.id}`}
                  value={merged.reach ?? ''}
                  onChange={(v) => updateField(c.id, 'reach', v)}
                  type="number"
                />
                <LabeledInput
                  label="Likes"
                  testid={`metric-likes-${c.id}`}
                  value={merged.likes ?? ''}
                  onChange={(v) => updateField(c.id, 'likes', v)}
                  type="number"
                />
                <LabeledInput
                  label="Comments"
                  testid={`metric-comments-${c.id}`}
                  value={merged.comments ?? ''}
                  onChange={(v) => updateField(c.id, 'comments', v)}
                  type="number"
                />
                <LabeledInput
                  label="Shares"
                  testid={`metric-shares-${c.id}`}
                  value={merged.shares ?? ''}
                  onChange={(v) => updateField(c.id, 'shares', v)}
                  type="number"
                />
                <LabeledInput
                  label="Saves"
                  testid={`metric-saves-${c.id}`}
                  value={merged.saves ?? ''}
                  onChange={(v) => updateField(c.id, 'saves', v)}
                  type="number"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-2 items-end">
                <LabeledInput
                  label="Notes (optional)"
                  testid={`metric-notes-${c.id}`}
                  value={merged.notes || ''}
                  onChange={(v) => updateField(c.id, 'notes', v)}
                  placeholder="Anything to highlight to the brand…"
                />
                <div className="flex items-center gap-2 justify-end">
                  {merged.live_post_link && (
                    <a
                      href={merged.live_post_link}
                      target="_blank"
                      rel="noreferrer"
                      className="text-[11px] text-[#06B6D4] hover:underline inline-flex items-center gap-1 font-mono"
                    >
                      <ExternalLink size={11} /> Open post
                    </a>
                  )}
                  <Button
                    onClick={() => saveRow(c.id)}
                    disabled={!isDirty || saving[c.id]}
                    className={`rounded-full px-4 text-xs h-8 inline-flex items-center gap-1.5 transition-colors ${
                      isDirty
                        ? 'bg-[#FF451A] text-[#0F0F0F] dark:text-white hover:bg-[#E63E17]'
                        : 'bg-white/5 text-[#6B6B6B] dark:text-zinc-500 border border-black/[0.10] dark:border-white/10 cursor-not-allowed'
                    }`}
                    data-testid={`metric-save-${c.id}`}
                  >
                    {saving[c.id] ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />}
                    {isDirty ? 'Save' : 'Saved'}
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// --- Sub-components ----------------------------------------------------------
const AggTile = ({ icon: Icon, label, value, sub, color, testid }) => (
  <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-xl p-3" data-testid={testid}>
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-500 mb-1 font-mono">
      <Icon size={10} style={{ color }} /> {label}
    </div>
    <div className="text-xl font-bold text-[#0F0F0F] dark:text-white font-mono" style={{ color }}>{value}</div>
    {sub && <div className="text-[10px] text-[#6B6B6B] dark:text-zinc-500 font-mono mt-0.5">{sub}</div>}
  </div>
);

const LabeledInput = ({ label, value, onChange, placeholder, type = 'text', className = '', testid }) => (
  <div className={className}>
    <label className="text-[10px] font-semibold uppercase tracking-wider block mb-1 font-mono text-[#0F0F0F] dark:text-white">
      {label}
    </label>
    <Input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="rounded-lg text-sm h-8 px-2 bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500"
      data-testid={testid}
    />
  </div>
);
